import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import has from 'lodash/has';
import IconButton from '@mui/material/IconButton';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Button from 'components/Button';
import Card from 'components/Card';
import HtmlContent from 'components/HtmlContent';
import NavLink from 'components/NavLink';
import useConfirm from 'components/Confirmation/useConfirm';
import { authSelectors } from 'redux/auth';
import { workspaceActions } from 'redux/workspace';
import { sanitizeNoticeHtml } from 'services/sanitizationService';
import { seeMoreProps } from 'services/urlService';
import noticeIcons from './noticeIcons';

const tonePalettes = { guava: 'guava', info: 'statusBlue', success: 'statusGreen', warning: 'statusYellow', critical: 'statusRed' };
const buttonVariants = ['contained', 'outlined', 'text'];
const buttonColors = ['primary', 'default', 'inherit', 'error', 'warning', 'success'];
const noActions = [];

const NoticeCard = styled(Card, { shouldForwardProp: prop => !['tone', 'pending'].includes(prop) })`
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem;

  ${({ theme, tone }) => has(tonePalettes, tone) && css`border-top: 4px solid ${theme.palette[tonePalettes[tone]].main};`}

  transition: opacity 0.2s;
  ${({ pending }) => pending && css`opacity: 0.6;`}
`;

const Clamp = styled('div', { shouldForwardProp: prop => !['height', 'faded'].includes(prop) })`
  position: relative;

  ${({ height }) => height && css`
    max-height: ${height}px;
    overflow: hidden;
  `}

  ${({ faded }) => faded && css`
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 4rem;
      background: linear-gradient(rgba(255, 255, 255, 0), #fff);
      pointer-events: none;
    }
  `}
`;

const Content = styled(HtmlContent, { shouldForwardProp: prop => prop !== 'closable' })`
  // Only the lines beside the close button make room for it, so embeds below keep the full width
  ${({ closable }) => closable && css`
    &::before {
      content: '';
      float: right;
      width: 2rem;
      height: 1.5rem;
    }
  `}

  > :first-child {
    margin-top: 0;
  }

  > :last-child {
    margin-bottom: 0;
  }

  iframe {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
  }
`;

const Actions = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const LastStartButton = styled(Button)`
  margin-right: auto;
`;

const ToggleButton = styled(Button)`
  align-self: flex-start;
`;

// The content is positioned too, so the button is lifted above it
const CloseButton = styled(IconButton)`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 1;
`;

const linkProps = (url) => {
  if (url.startsWith('/')) {
    return { component: NavLink, to: url };
  }

  const props = seeMoreProps(url);

  return props.component === 'a' ? { ...props, target: '_blank', rel: 'noopener noreferrer' } : props;
};

const useTallerThan = (ref, height, enabled) => {
  const [taller, setTaller] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;

    if (!enabled || !element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    // Images and embeds load after the first layout, so the content is watched rather than measured once
    const observer = new ResizeObserver(() => setTaller(element.offsetHeight > height));
    observer.observe(element);

    return () => observer.disconnect();
  }, [ref, height, enabled]);

  return taller;
};

const NoticeWidget = ({ uuid, tone, html = '', closable = false, compactHeight, actions = noActions, pending = false, ...rest }) => {

  const { t } = useTranslation(['common']);
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const workspaceId = useSelector(authSelectors.getWorkspaceId);

  const content = useMemo(() => ({ __html: sanitizeNoticeHtml(html) }), [html]);

  const contentRef = useRef();
  const [expanded, setExpanded] = useState(false);
  const compact = compactHeight > 0;
  const overflows = useTallerThan(contentRef, compactHeight, compact);
  const clamped = compact && !expanded;

  const [orderedActions, lastStartIndex] = useMemo(() => {
    const start = actions.filter(({ placement }) => placement === 'start');

    return [[...start, ...actions.filter(({ placement }) => placement !== 'start')], start.length - 1];
  }, [actions]);

  const respond = action => dispatch(workspaceActions.respondToNotice({ workspaceId, uuid, action }));

  const onAction = ({ key, label, confirm: question }) => {
    if (!question) {
      respond(key);
      return;
    }

    confirm({ title: question, submit: label }).then(() => respond(key)).catch(() => {});
  };

  const renderAction = (action, index) => {
    const ActionButton = index === lastStartIndex ? LastStartButton : Button;
    const Icon = has(noticeIcons, action.icon) ? noticeIcons[action.icon] : null;

    return (
      <ActionButton
        key={action.key}
        size="small"
        variant={buttonVariants.includes(action.variant) ? action.variant : 'text'}
        color={buttonColors.includes(action.color) ? action.color : 'primary'}
        startIcon={Icon && <Icon />}
        disabled={pending}
        onClick={() => onAction(action)}
        {...(action.url && !action.confirm && linkProps(action.url))}
      >
        {action.label ?? action.key}
      </ActionButton>
    );
  };

  return (
    <div {...rest}>
      <NoticeCard component="section" tone={tone} pending={pending} aria-busy={pending}>
        {closable && (
          <CloseButton size="small" aria-label={t('noticeWidget.close')} title={t('noticeWidget.close')} disabled={pending} onClick={() => respond('dismiss')}>
            <CloseRoundedIcon fontSize="small" />
          </CloseButton>
        )}

        <Clamp height={clamped ? compactHeight : null} faded={clamped && overflows}>
          <div ref={contentRef}>
            <Content closable={closable} dangerouslySetInnerHTML={content} />
          </div>
        </Clamp>

        {compact && overflows && (
          <ToggleButton variant="text" size="small" aria-expanded={expanded} onClick={() => setExpanded(e => !e)}>
            {expanded ? t('noticeWidget.showLess') : t('noticeWidget.showMore')}
          </ToggleButton>
        )}

        {actions.length > 0 && (
          <Actions>{orderedActions.map(renderAction)}</Actions>
        )}
      </NoticeCard>
    </div>
  );
};

NoticeWidget.propTypes = {
  uuid: PropTypes.string.isRequired,
  tone: PropTypes.string,
  html: PropTypes.string,
  closable: PropTypes.bool,
  compactHeight: PropTypes.number,
  actions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string,
    variant: PropTypes.string,
    color: PropTypes.string,
    icon: PropTypes.string,
    url: PropTypes.string,
    confirm: PropTypes.string,
    placement: PropTypes.string,
  })),
  pending: PropTypes.bool,
};

export default NoticeWidget;
